import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ArrowRight, Sparkles, Zap, Play, TrendingUp, Clock, Award } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { updateMetaTags } from '@/utils/seoUtils';

const MotionGraphicsService = () => {
  const { currentLanguage } = useLanguage();
  const { city } = useParams<{ city?: string }>();

  useEffect(() => {
    const title = {
      de: `Motion Graphics & Animation ${city ? `in ${city}` : 'Schweiz'} | Future Level`,
      en: `Motion Graphics & Animation ${city ? `in ${city}` : 'Switzerland'} | Future Level`,
      fr: `Motion Graphics & Animation ${city ? `à ${city}` : 'Suisse'} | Future Level`,
      it: `Motion Graphics & Animazione ${city ? `a ${city}` : 'Svizzera'} | Future Level`
    };

    const description = {
      de: `Professionelle Motion Graphics für Schweizer Unternehmen. Logo-Animationen, Erklärvideo-Intros, Werbegrafiken. Modernes Branding für KMU und Konzerne.`,
      en: `Professional motion graphics for Swiss businesses. Logo animations, explainer video intros, ad graphics. Modern branding for SMEs and corporations.`,
      fr: `Motion graphics professionnels pour entreprises suisses. Animations logo, intros vidéos explicatives, graphiques publicitaires. Branding moderne pour PME et entreprises.`,
      it: `Motion graphics professionali per aziende svizzere. Animazioni logo, intro video esplicativi, grafiche pubblicitarie. Branding moderno per PMI e aziende.`
    };

    updateMetaTags({
      title: title[currentLanguage],
      description: description[currentLanguage],
      canonicalUrl: `https://futurelevel.ch/${currentLanguage}/motion-graphics${city ? `/${city}` : ''}/`,
      language: currentLanguage
    });
  }, [currentLanguage, city]);

  const services = [
    {
      icon: Sparkles,
      title: {
        de: 'Logo-Animationen',
        en: 'Logo Animations',
        fr: 'Animations Logo',
        it: 'Animazioni Logo'
      },
      description: {
        de: 'Dynamische Logo-Animationen für Videos, Websites und Präsentationen',
        en: 'Dynamic logo animations for videos, websites and presentations',
        fr: 'Animations de logo dynamiques pour vidéos, sites web et présentations',
        it: 'Animazioni logo dinamiche per video, siti web e presentazioni'
      },
      examples: ['Video Intros', 'Website Headers', 'Social Media', 'Präsentationen'],
      price: 'CHF 400-1,200',
      deliveryTime: '3-5 Tage'
    },
    {
      icon: Play,
      title: {
        de: 'Erklärvideo-Intros',
        en: 'Explainer Video Intros',
        fr: 'Intros Vidéos Explicatives',
        it: 'Intro Video Esplicativi'
      },
      description: {
        de: 'Professionelle Intros und Outros für Unternehmensvideos und Tutorials',
        en: 'Professional intros and outros for corporate videos and tutorials',
        fr: 'Intros et outros professionnels pour vidéos corporate et tutoriels',
        it: 'Intro e outro professionali per video aziendali e tutorial'
      },
      examples: ['Corporate Videos', 'Training Videos', 'YouTube Content', 'Webinare'],
      price: 'CHF 600-2,000',
      deliveryTime: '5-7 Tage'
    },
    {
      icon: Zap,
      title: {
        de: 'Werbegrafiken & Social Media',
        en: 'Ad Graphics & Social Media',
        fr: 'Graphiques Publicitaires & Médias Sociaux',
        it: 'Grafiche Pubblicitarie & Social Media'
      },
      description: {
        de: 'Animierte Grafiken für Google Ads, Facebook Ads und Instagram Stories',
        en: 'Animated graphics for Google Ads, Facebook Ads and Instagram Stories',
        fr: 'Graphiques animés pour Google Ads, Facebook Ads et Instagram Stories',
        it: 'Grafiche animate per Google Ads, Facebook Ads e Instagram Stories'
      },
      examples: ['Google Display Ads', 'Meta Ads', 'Instagram Stories', 'LinkedIn Posts'],
      price: 'CHF 250-800',
      deliveryTime: '2-4 Tage'
    }
  ];

  const swissMarketFocus = [
    {
      sector: {
        de: 'Finanzdienstleister',
        en: 'Financial Services',
        fr: 'Services Financiers',
        it: 'Servizi Finanziari'
      },
      need: {
        de: 'Vertrauensvolle, seriöse Animationen für komplexe Finanzprodukte',
        en: 'Trustworthy, serious animations for complex financial products',
        fr: 'Animations sérieuses et fiables pour produits financiers complexes',
        it: 'Animazioni serie e affidabili per prodotti finanziari complessi'
      },
      examples: ['UBS', 'Credit Suisse', 'PostFinance', 'Raiffeisen'],
      avgBudget: 'CHF 2,000-8,000'
    },
    {
      sector: {
        de: 'Tech-Startups',
        en: 'Tech Startups',
        fr: 'Startups Tech',
        it: 'Startup Tech'
      },
      need: {
        de: 'Moderne, dynamische Animationen für App-Präsentationen und Pitches',
        en: 'Modern, dynamic animations for app presentations and pitches',
        fr: 'Animations modernes et dynamiques pour présentations app et pitches',
        it: 'Animazioni moderne e dinamiche per presentazioni app e pitch'
      },
      examples: ['Crypto Valley', 'ETH Spinoffs', 'Fintech', 'Healthtech'],
      avgBudget: 'CHF 1,500-5,000'
    },
    {
      sector: {
        de: 'Konsumgüter & Retail',
        en: 'Consumer Goods & Retail',
        fr: 'Biens de Consommation & Retail',
        it: 'Beni di Consumo & Retail'
      },
      need: {
        de: 'Emotionale, verkaufsfördernde Animationen für Produktkampagnen',
        en: 'Emotional, sales-boosting animations for product campaigns',
        fr: 'Animations émotionnelles boostant les ventes pour campagnes produits',
        it: 'Animazioni emozionali che aumentano le vendite per campagne prodotti'
      },
      examples: ['Migros', 'Coop', 'Manor', 'Galaxus'],
      avgBudget: 'CHF 3,000-12,000'
    }
  ];

  const benefits = [
    {
      icon: Clock,
      title: {
        de: 'Express-Lieferung',
        en: 'Express Delivery',
        fr: 'Livraison Express',
        it: 'Consegna Express'
      },
      description: {
        de: '48h Express-Service für dringende Kampagnen und Produktlaunches',
        en: '48h express service for urgent campaigns and product launches',
        fr: 'Service express 48h pour campagnes urgentes et lancements produits',
        it: 'Servizio express 48h per campagne urgenti e lanci prodotti'
      }
    },
    {
      icon: Award,
      title: {
        de: 'Swiss Branding Standards',
        en: 'Swiss Branding Standards',
        fr: 'Standards Branding Suisse',
        it: 'Standard Branding Svizzero'
      },
      description: {
        de: 'Hochwertige Animationen, die Schweizer Qualitäts- und Design-Standards entsprechen',
        en: 'High-quality animations that meet Swiss quality and design standards',
        fr: 'Animations haute qualité respectant standards qualité et design suisses',
        it: 'Animazioni di alta qualità che rispettano standard qualità e design svizzeri'
      }
    },
    {
      icon: TrendingUp,
      title: {
        de: 'Performance-Optimiert',
        en: 'Performance-Optimized',
        fr: 'Optimisé Performance',
        it: 'Ottimizzato Prestazioni'
      },
      description: {
        de: 'Alle Animationen optimiert für maximale Engagement-Raten und Conversions',
        en: 'All animations optimized for maximum engagement rates and conversions',
        fr: 'Toutes animations optimisées pour taux engagement et conversions maximaux',
        it: 'Tutte le animazioni ottimizzate per massimi tassi engagement e conversioni'
      }
    }
  ];

  const pricingPackages = [
    {
      name: {
        de: 'Startup Paket',
        en: 'Startup Package',
        fr: 'Pack Startup',
        it: 'Pacchetto Startup'
      },
      price: 'CHF 800',
      description: {
        de: 'Perfekt für junge Unternehmen und erste Markenpräsenz',
        en: 'Perfect for young companies and first brand presence',
        fr: 'Parfait pour jeunes entreprises et première présence marque',
        it: 'Perfetto per giovani aziende e prima presenza del brand'
      },
      includes: [
        'Logo-Animation (5 Sekunden)',
        '3 Social Media Formate',
        '2 Revisionen',
        'HD-Export (1080p)'
      ]
    },
    {
      name: {
        de: 'Business Paket',
        en: 'Business Package',
        fr: 'Pack Business',
        it: 'Pacchetto Business'
      },
      price: 'CHF 2,200',
      description: {
        de: 'Umfassende Motion Graphics für etablierte Unternehmen',
        en: 'Comprehensive motion graphics for established companies',
        fr: 'Motion graphics complets pour entreprises établies',
        it: 'Motion graphics complete per aziende consolidate'
      },
      includes: [
        'Logo-Animation + Varianten',
        'Video Intro/Outro Set',
        '6 Social Media Formate',
        '4 Revisionen',
        '4K-Export + Web-Formate'
      ],
      popular: true
    },
    {
      name: {
        de: 'Enterprise Paket',
        en: 'Enterprise Package',
        fr: 'Pack Enterprise',
        it: 'Pacchetto Enterprise'
      },
      price: 'CHF 5,500',
      description: {
        de: 'Premium Motion Graphics Suite für Konzerne und Kampagnen',
        en: 'Premium motion graphics suite for corporations and campaigns',
        fr: 'Suite motion graphics premium pour entreprises et campagnes',
        it: 'Suite motion graphics premium per aziende e campagne'
      },
      includes: [
        'Komplettes Brand Animation Kit',
        'Kampagnen-Grafiken Set',
        'Unbegrenzte Revisionen',
        'Alle Formate + Raw Files',
        'Brand Guidelines Dokument'
      ]
    }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow pt-20">
        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-br from-background to-background/80 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/20 via-background to-background"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge variant="outline" className="mb-4">
                <Sparkles className="w-4 h-4 mr-2" />
                {currentLanguage === 'de' ? 'Motion Graphics' : 
                 currentLanguage === 'fr' ? 'Motion Graphics' :
                 currentLanguage === 'it' ? 'Motion Graphics' : 'Motion Graphics'}
              </Badge>
              
              <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">
                {currentLanguage === 'de' ? 'Bewegung. Branding. Brillanz.' :
                 currentLanguage === 'fr' ? 'Mouvement. Branding. Brillance.' :
                 currentLanguage === 'it' ? 'Movimento. Branding. Brillantezza.' :
                 'Motion. Branding. Brilliance.'}
              </h1>
              
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 leading-relaxed">
                {currentLanguage === 'de' ? 'Professionelle Motion Graphics, die Schweizer Marken zum Leben erwecken. Von Logo-Animationen bis Corporate Video Intros.' :
                 currentLanguage === 'fr' ? 'Motion graphics professionnels qui donnent vie aux marques suisses. Des animations logo aux intros vidéos corporate.' :
                 currentLanguage === 'it' ? 'Motion graphics professionali che danno vita ai brand svizzeri. Dalle animazioni logo agli intro video aziendali.' :
                 'Professional motion graphics that bring Swiss brands to life. From logo animations to corporate video intros.'}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <Button size="lg" className="bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary">
                  {currentLanguage === 'de' ? 'Demo ansehen' :
                   currentLanguage === 'fr' ? 'Voir Démo' :
                   currentLanguage === 'it' ? 'Vedi Demo' :
                   'View Demo'}
                  <ArrowRight className="ml-2" size={20} />
                </Button>
                <Button variant="outline" size="lg">
                  {currentLanguage === 'de' ? 'Kostenloses Angebot' :
                   currentLanguage === 'fr' ? 'Devis Gratuit' :
                   currentLanguage === 'it' ? 'Preventivo Gratuito' :
                   'Free Quote'}
                </Button>
              </div>

              {/* Performance Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">2,500+</div>
                  <div className="text-sm text-muted-foreground">
                    {currentLanguage === 'de' ? 'Animationen' :
                     currentLanguage === 'fr' ? 'Animations' :
                     currentLanguage === 'it' ? 'Animazioni' :
                     'Animations'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">48h</div>
                  <div className="text-sm text-muted-foreground">
                    {currentLanguage === 'de' ? 'Express-Service' :
                     currentLanguage === 'fr' ? 'Service Express' :
                     currentLanguage === 'it' ? 'Servizio Express' :
                     'Express Service'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">95%</div>
                  <div className="text-sm text-muted-foreground">
                    {currentLanguage === 'de' ? 'Kundenzufriedenheit' :
                     currentLanguage === 'fr' ? 'Satisfaction Client' :
                     currentLanguage === 'it' ? 'Soddisfazione Cliente' :
                     'Client Satisfaction'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">200+</div>
                  <div className="text-sm text-muted-foreground">
                    {currentLanguage === 'de' ? 'Swiss Brands' :
                     currentLanguage === 'fr' ? 'Marques Suisses' :
                     currentLanguage === 'it' ? 'Brand Svizzeri' :
                     'Swiss Brands'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                {currentLanguage === 'de' ? 'Motion Graphics Services' :
                 currentLanguage === 'fr' ? 'Services Motion Graphics' :
                 currentLanguage === 'it' ? 'Servizi Motion Graphics' :
                 'Motion Graphics Services'}
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8">
                {services.map((service, index) => {
                  const Icon = service.icon;
                  return (
                    <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                      <div className="text-center mb-4">
                        <Icon className="w-12 h-12 text-primary mx-auto mb-4" />
                        <h3 className="text-xl font-semibold mb-2">
                          {service.title[currentLanguage]}
                        </h3>
                        <p className="text-muted-foreground mb-4">
                          {service.description[currentLanguage]}
                        </p>
                        
                        <div className="grid grid-cols-2 gap-1 mb-4 text-xs">
                          {service.examples.map((example, idx) => (
                            <Badge key={idx} variant="secondary" className="justify-center text-xs">
                              {example}
                            </Badge>
                          ))}
                        </div>
                        
                        <div className="flex justify-between items-center mb-4 text-sm">
                          <span className="text-muted-foreground">
                            {service.deliveryTime}
                          </span>
                          <span className="text-lg font-bold text-primary">
                            {service.price}
                          </span>
                        </div>
                        
                        <Button className="w-full">
                          {currentLanguage === 'de' ? 'Mehr erfahren' :
                           currentLanguage === 'fr' ? 'En savoir plus' :
                           currentLanguage === 'it' ? 'Scopri di più' :
                           'Learn More'}
                        </Button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Swiss Market Focus */}
        <section className="py-16 bg-background/50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                {currentLanguage === 'de' ? 'Schweizer Märkte, die Motion Graphics brauchen' :
                 currentLanguage === 'fr' ? 'Marchés Suisses qui ont besoin de Motion Graphics' :
                 currentLanguage === 'it' ? 'Mercati Svizzeri che necessitano Motion Graphics' :
                 'Swiss Markets That Need Motion Graphics'}
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8">
                {swissMarketFocus.map((market, index) => (
                  <Card key={index} className="p-6">
                    <h3 className="text-xl font-semibold mb-3">
                      {market.sector[currentLanguage]}
                    </h3>
                    <p className="text-muted-foreground mb-4 text-sm">
                      {market.need[currentLanguage]}
                    </p>
                    <div className="mb-4">
                      <div className="text-sm font-medium mb-2">
                        {currentLanguage === 'de' ? 'Beispielkunden:' :
                         currentLanguage === 'fr' ? 'Exemples clients:' :
                         currentLanguage === 'it' ? 'Esempi clienti:' :
                         'Example clients:'}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {market.examples.map((example, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {example}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="text-lg font-bold text-primary">
                      {market.avgBudget}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Packages */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                {currentLanguage === 'de' ? 'Motion Graphics Pakete' :
                 currentLanguage === 'fr' ? 'Packs Motion Graphics' :
                 currentLanguage === 'it' ? 'Pacchetti Motion Graphics' :
                 'Motion Graphics Packages'}
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8">
                {pricingPackages.map((pkg, index) => (
                  <Card key={index} className={`p-6 relative ${pkg.popular ? 'border-primary shadow-lg' : ''}`}>
                    {pkg.popular && (
                      <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-primary">
                        {currentLanguage === 'de' ? 'Beliebt' :
                         currentLanguage === 'fr' ? 'Populaire' :
                         currentLanguage === 'it' ? 'Popolare' :
                         'Popular'}
                      </Badge>
                    )}
                    <div className="text-center mb-6">
                      <h3 className="text-xl font-semibold mb-2">
                        {pkg.name[currentLanguage]}
                      </h3>
                      <div className="text-3xl font-bold text-primary mb-2">
                        {pkg.price}
                      </div>
                      <p className="text-muted-foreground text-sm">
                        {pkg.description[currentLanguage]}
                      </p>
                    </div>
                    
                    <div className="space-y-2 mb-6">
                      {pkg.includes.map((item, idx) => (
                        <div key={idx} className="flex items-center text-sm">
                          <ArrowRight className="w-4 h-4 text-primary mr-2 flex-shrink-0" />
                          {item}
                        </div>
                      ))}
                    </div>
                    
                    <Button className={`w-full ${pkg.popular ? 'bg-primary' : ''}`}>
                      {currentLanguage === 'de' ? 'Paket wählen' :
                       currentLanguage === 'fr' ? 'Choisir Pack' :
                       currentLanguage === 'it' ? 'Scegli Pacchetto' :
                       'Choose Package'}
                    </Button>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 bg-background/50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                {currentLanguage === 'de' ? 'Warum unsere Motion Graphics?' :
                 currentLanguage === 'fr' ? 'Pourquoi nos Motion Graphics?' :
                 currentLanguage === 'it' ? 'Perché le nostre Motion Graphics?' :
                 'Why Our Motion Graphics?'}
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8">
                {benefits.map((benefit, index) => {
                  const Icon = benefit.icon;
                  return (
                    <div key={index} className="text-center">
                      <Icon className="w-12 h-12 text-primary mx-auto mb-4" />
                      <h3 className="text-xl font-semibold mb-2">
                        {benefit.title[currentLanguage]}
                      </h3>
                      <p className="text-muted-foreground">
                        {benefit.description[currentLanguage]}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-primary/10 to-accent/10">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-6">
              {currentLanguage === 'de' ? 'Bereit, Ihre Marke zu animieren?' :
               currentLanguage === 'fr' ? 'Prêt à animer votre marque?' :
               currentLanguage === 'it' ? 'Pronti ad animare il vostro brand?' :
               'Ready to Animate Your Brand?'}
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              {currentLanguage === 'de' ? 'Lassen Sie uns Ihr Logo und Ihre Inhalte zum Leben erwecken. Motion Graphics, die im Gedächtnis bleiben.' :
               currentLanguage === 'fr' ? 'Laissez-nous donner vie à votre logo et vos contenus. Motion graphics qui marquent les esprits.' :
               currentLanguage === 'it' ? 'Lasciaci dare vita al vostro logo e ai vostri contenuti. Motion graphics che rimangono impressi.' :
               'Let us bring your logo and content to life. Motion graphics that stick in memory.'}
            </p>
            <Button size="lg" asChild className="bg-gradient-to-r from-primary to-primary/90">
              <a href={`/${currentLanguage}/kontakt/`}>
                {currentLanguage === 'de' ? 'Animation starten' :
                 currentLanguage === 'fr' ? 'Démarrer animation' :
                 currentLanguage === 'it' ? 'Inizia animazione' :
                 'Start Animation'}
                <ArrowRight className="ml-2" size={20} />
              </a>
            </Button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default MotionGraphicsService;